<!--主播信息栏-->
<template>
  <div class="room-info">
    <div class="room-info-lf">
      <!-- 点击主播头像跳个人主页 -->
      <span class="anchor-avatar">
        <img
          class="anchor-avatar__img"
          :src="`${roomInfo.primary.face}@64w_64h_1e_1c_1f.webp`"
          @click="onClickAnchorAvatar"
        />
      </span>
      <div class="room-anchor-info">
        <div class="room-title">{{ roomInfo.title }}</div>
        <div class="anchor-info">
          <!--<div class="anchor-level"></div>-->
          <!-- 点击主播昵称跳个人主页 -->
          <div class="anchor-name" @click="onClickAnchorName">
            {{ roomInfo.primary.name }}
          </div>
          <div class="charge-strip-container">
            <charge-strip :room-info="roomInfo" :room-id="roomId"/>
          </div>
        </div>
      </div>
    </div>
    <div class="room-info-rt">
      <div class="popularity">
        <div class="popularity-icon"></div>
        <div class="popularity-value">{{ onlineFormatted }}</div>
      </div>
      <div class="room-actions">
        <div class="operation-icon" />
        <ul class="action-list">
          <li class="tooltip-operation-item" @click="copyRoomURL">
            {{ $t('roomInfo.share') }}
          </li>
          <!--  关闭举报入口  -->
          <!--  <li class="tooltip-operation-item" @click="reportRoom">
            {{ $t("roomInfo.report") }}
          </li> -->
        </ul>
      </div>
      <div class="follow">
        <div
          v-if="!roomInfo.primary.attention" 
          class="follow-status unfollow" 
          @click="follow"
        >
          {{ $t('follow.follow') }}
        </div>
        <div v-else 
          class="follow-status followed"
        >
          {{ $t('follow.following') }}
        </div>
        <div class="follow-value">{{ followersFormatted }}</div>
      </div>
    </div>
  </div>
</template>


<script lang="ts" setup >
import { toRefs, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useLogin } from '@bilibili-intl/login';
import { clipboard } from '@/utils';
import toast from '@/components/toast';
import type { IRoomInfoResponseData } from '@/service/room/types';
import userService from '@/service/user';
import { useI18n } from 'vue-i18n';
import roomService from '@/service/room';
import { roomTracker } from '@/track/room';
import { URLManager } from '@/common/url-manager';
import ChargeStrip from './charge-strip.vue';


const props = defineProps<{
  roomInfo: IRoomInfoResponseData;
  roomId: number;
}>();

const { t: $t } = useI18n();

const { roomInfo, roomId } = toRefs(props);

const store = useStore();

const isLogin = computed(() => !!store.state.global.userInfo?.is_login);

const loginContext = useLogin();

const onlineFormatted = computed(() => {
  const onlineNum = Number(roomInfo.value.online);
  if (roomInfo.value.state === 0) {
    return '--';
  }
  if (onlineNum === 0) {
    return '--';
  }
  if (onlineNum < 1e6) {
    return onlineNum;
  } else if (onlineNum < 1e9) {
    return (onlineNum / 1e6).toFixed(2) + 'M';
  } else {
    return (onlineNum / 1e9).toFixed(2) + 'B';
  }
});

const followersFormatted = computed(() => {
  const followers = Number(roomInfo.value.primary.followers);

  if (followers < 1e6) {
    return followers;
  } else if (followers < 1e9) {
    return (followers / 1e6).toFixed(2) + 'M';
  } else {
    return (followers / 1e9).toFixed(2) + 'B';
  }
});

const copyRoomURL = () => {
  roomTracker.ShareClick(roomId.value);
  const roomURL = window.location.href;
  clipboard(roomURL);
  toast({
    message: $t('roomInfo.copySucceed'),
    appendTo: '.video-container',
    duration: 3000,
  });
};

// 举报直播间
const reportRoom = () => {
  if (isLogin.value) {
    emitter.emit(EB_EVENT_NAME.OPEN_REPORT_MODAL, {
      roomId: roomId.value,
      type: 'room',
    } as IOpenReportModalData);
  } else {
    emitter.emit(EB_EVENT_NAME.NEED_LOGIN);
  }
};

let followClicking = false;

const follow = async () => {
  if (!isLogin.value) {
    loginContext.openLogin();
    return;
  }
  roomTracker.FollowClick(roomId.value, roomInfo.value.primary.mid);
  followClicking = true;
  try {
    await userService.follow(roomInfo.value.primary.mid, 1, roomId.value);
    roomInfo.value.primary.attention = 1;
    const followers = Number(roomInfo.value.primary.followers);
    if (!Number.isNaN(followers)) {
      roomInfo.value.primary.followers = (followers + 1).toString();
    }
  } catch (e: any) {
    e?.message && toast(e.message);
  } finally {
    followClicking = false;
  }
};

const unfollow = async () => {
  try {
    await userService.follow(roomInfo.value.primary.mid, 2, roomId.value);
    roomInfo.value.primary.attention = 0;
    const followers = Number(roomInfo.value.primary.followers);
    if (!Number.isNaN(followers)) {
      roomInfo.value.primary.followers = (followers - 1).toString();
    }
  } catch (e:any) {
    e?.message && toast(e.message);
  }
};

let pollContinuousErrorCnt = 0;
const pollOnline = async () => {
  try {
    const { data } = await roomService.getRoomOnline(roomId.value);
    const { online, online_interval } = data;
    const prevOnlineNum = Number(roomInfo.value.online);
    const nextOnlineNum = Number(online);
    if (Number.isNaN(prevOnlineNum) || Number.isNaN(nextOnlineNum)) {
      roomInfo.value.online_interval = online_interval;
      roomInfo.value.online = online;
    } else {
      const totalSteps = 5;
      const increasePerStep = (nextOnlineNum - prevOnlineNum) / totalSteps;
      const stepInterval = online_interval / totalSteps;
      let currentStep = 0;
      let currentOnlineNum = prevOnlineNum;

      const setOnlineByStep = () => {
        currentStep++;
        currentOnlineNum += increasePerStep;
        if (currentStep < totalSteps) {
          roomInfo.value.online = (~~currentOnlineNum).toString();
          setTimeout(setOnlineByStep, stepInterval * 1000);
        } else if (currentStep === totalSteps) {
          // 最后一次
          roomInfo.value.online = online;
        }
      };
      setOnlineByStep();
      pollContinuousErrorCnt = 0;
    }
  } catch (e) {
    pollContinuousErrorCnt++;
  } finally {
    if (pollContinuousErrorCnt < 5) {
      setTimeout(pollOnline, roomInfo.value.online_interval * 1000);
    }
  }
};

onMounted( () => {
  setTimeout(pollOnline, roomInfo.value.online_interval * 1000);
});

const onClickAnchorName = () => {
  roomTracker.AnchorNicknameClick(roomId.value, roomInfo.value.primary.mid);
  setTimeout(
    () => (URLManager.goto({url: '../space/' + roomInfo.value.primary.mid, isBlank: true})),
    300
  );
};

const onClickAnchorAvatar = () => {
  roomTracker.AnchorIconClick(roomId.value, roomInfo.value.primary.mid);
  setTimeout(
    () => (URLManager.goto({url: '../space/' + roomInfo.value.primary.mid, isBlank: true})),
    300
  );
};
</script>


<style lang="less" scoped>
.room-info {
  width: 100%;
  height: 96px;
  background: #fff;
  border-radius: 8px 8px 0 0;
  padding: 9px 12px 9px 9px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .room-info-lf {
    display: inline-flex;
    align-items: center;
    flex-shrink: 1;
    width: calc(100% - 190px);

    .anchor-avatar {
      display: block;
      width: 78px;
      height: 78px;
      padding: 15px;
      //background: #f8f8f8;
      flex-shrink: 0;
      cursor: pointer;

      .anchor-avatar__img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        display: block;
      }
    }

    .room-anchor-info {
      margin-left: 5px;
      flex-shrink: 1;
      width: calc(100% - 80px);

      .room-title {
        font-size: 18px;
        color: #333;
        letter-spacing: 0;
        line-height: 22px;
        font-weight: 700;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .anchor-info {
        margin-top: 10px;
        display: flex;
        align-items: center;

        .anchor-level {
          width: 55px;
          height: 18px;
          background: url("@/assets/level-icon.svg") no-repeat;
          background-size: 100%;
          border-radius: 4px;
          font-size: 12px;
          color: #ffeacf;
          font-weight: 400;
          text-align: center;
          line-height: 18px;
        }

        .anchor-name {
          display: block;
          font-size: 14px;
          color: #333;
          letter-spacing: 0;
          line-height: 18px;
          font-weight: 400;
          //margin-left: 9px;
          text-decoration: none;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          cursor: pointer;
          flex-shrink: 1;

          &:hover {
            color: #4c93ff;
          }
        }

        .charge-strip-container {
          flex-shrink: 0;
          padding-left: 16px;
        }
      }
    }
  }

  .room-info-rt {
    width: 190px;
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    align-self: center;
    flex-wrap: wrap;
    justify-content: flex-end;

    .popularity {
      display: flex;
      justify-content: flex-end;
      align-items: center;

      .popularity-icon {
        width: 18px;
        height: 18px;
        background: url("@/assets/popularity-icon.png") no-repeat;
        background-size: 100%;
      }

      .popularity-value {
        margin-left: 4px;
        font-size: 14px;
        color: #333;
        letter-spacing: 0;
        text-align: right;
        line-height: 18px;
        font-weight: 400;
      }

      :deep(.tooltip-operation-popper) {
        z-index: 9999 !important;
      }
    }

    .room-actions {
      margin-left: 12px;
      position: relative;

      .operation-icon {
        width: 18px;
        height: 18px;
        background: url("@/assets/more.svg") no-repeat;
        background-size: 100%;
        cursor: pointer;
      }

      .action-list {
        position: absolute;
        right: 0;
        top: 95%;
        display: block;
        background-color: #fff;
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 0 12px;
        opacity: 0;
        transition: opacity 0.3s ease;
        height: 0;
        overflow: hidden;
        border: none;

        & > li {
          color: #4c93ff;
          font-size: 14px;
          letter-spacing: 0;
          line-height: 18px;
          font-weight: 400;
          padding-left: 30px;
          background: url("@/assets/icon_share.png") no-repeat left center;
          background-size: 20px;
          cursor: pointer;

          & + li {
            margin-top: 6px;
          }
        }
      }

      &:hover {
        .action-list {
          opacity: 1;
          height: auto;
          border: 1px solid #ddd;
          padding: 6px 12px;
        }
      }
    }

    .follow {
      margin-top: 14px;
      font-size: 0;
      height: 26px;
      line-height: 25px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      text-align: center;

      .follow-status {
        display: inline-block;
        height: 100%;
        padding: 0 12px;
        border-radius: 4px 0 0 4px;
        border: 1px solid #4c93ff;
        font-size: 14px;
        text-align: center;
        font-weight: 700;

        &.unfollow {
          cursor: pointer;
          color: #fff;
          background: #4c93ff;
        }

        &.followed {
          color: #4c93ff;
          background: #fff;
          border-right: none;
        }
      }

      .follow-value {
        display: inline-block;
        height: 100%;
        padding: 0 12px;
        border: 1px solid rgba(76, 147, 255, 1);
        border-radius: 0 4px 4px 0;
        font-size: 14px;
        color: #333;
        letter-spacing: 0;
        font-weight: 400;
        min-width: 68px;
      }
    }
  }
}

// .tooltip-operation {
//   width: 150px;

//   .tooltip-operation-item {
//     font-size: 14px;
//     color: #4c93ff;
//     letter-spacing: 0;
//     line-height: 18px;
//     font-weight: 400;
//     padding-left: 30px;
//     background: url("@/assets/icon_share.png") no-repeat left center;
//     background-size: 20px;
//     cursor: pointer;
//   }
// }
</style>
